/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-02-02 16:21:40
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Icon, TextStyle, Link } from '@shopify/polaris';
import { Field } from 'redux-form';
import { Collapse } from 'react-collapse';
import { CaretDownMinor, ExternalMinor } from '@shopify/polaris-icons';
import TextForm from './text-form';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import RenderColorPickerItem from 'src/components/views/common/render-color-picker-item';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import CustomDropZoneStandard from 'src/components/views/common/custom-dropzone-standard-v2';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';

import {
  SHOW_BUTTON_OPTIONS,
  BUTTON_TYPE_OPTIONS,
  BUTTON_SHAPE_OPTIONS,
  ICON_POSITION_OPTIONS,
  BUTTON_WIDTH_OPTIONS,
  DISPLAY_TYPE_OPTIONS,
  WIDTH_UNIT_OPTIONS,
  SELECT_OPTIONS_ACTION_OPTIONS,
  BUTTON_WIDTH_VALUE,
  BUTTON_TYPE_VALUE,
  DISPLAY_TYPE_VALUE,
  SELECT_OPTIONS_ACTION_VALUE,
  ELEMENTS,
  MAX_FILE_SIZE,
  ACCEPT_FILE_IMAGE_TYPE
} from 'src/constants/theme-settings';
import { BUTTON_ALIGNMENT_OPTIONS, MENU_ID } from 'src/constants/theme-settings.jsx';
import { DropdownIndicator, styles, getStylesCustom } from './react-select-style';
import { normalizeNumber, nonXSSv2, isLimitFileSize } from 'src/helpers/form-validation-helper';
import { isNumeric, checkHasXss, convertImageToBase64 } from 'src/helpers';
import { URL } from 'src/constants/links';

type Props = {
  formValues: any,
  themeSettingDefaultValue: any,
  themeSettingDefaultThemeValue: any,
  labelTranslation: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

const nonXSSValue = nonXSSv2();
const limitSizeValidation = isLimitFileSize();

class ProductButtonForm extends Component<ComposedProps> {
  state = {
    errorUpload: '',
    isOpenOverall: true,
    isOpenDimension: true,
    isOpenAlignment: true,
    isOpenStyling: true,
    isOpenButtonStyle: true
  };

  componentWillReceiveProps(nextProps) {
    const { prefix: activeMenuItem } = this.props;
    const { prefix: nextActiveMenuItem, updateField } = nextProps;
    if (activeMenuItem !== nextActiveMenuItem) {
      this.setState({
        isOpenOverall: true,
        isOpenDimension: true,
        isOpenAlignment: true,
        isOpenStyling: true,
        isOpenButtonStyle: true
      });

      const scrollDiv = document.querySelector('.visual-editor-right-panel__scroll');
      scrollDiv.scrollTop = 0;

      // Remove invalid image size when change component button
      if (this.state.errorUpload) {
        updateField(`${activeMenuItem}.imgSrc`, '');
        updateField(`${activeMenuItem}.imgSize`, '');
        updateField(`${activeMenuItem}.imgName`, '');
        updateField(`${activeMenuItem}.imgSrc64`, '');
      }
    }
  }

  getFilterButtonTypeOptions = () => {
    const { prefix: activeMenuItem, formValues } = this.props;
    const [, menuId] = activeMenuItem.split('_');
    const otherButtonKeys = _(formValues)
      .keys()
      .filter(key => key.includes('button') && !key.includes(menuId))
      .value();

    const otherButtonValues = otherButtonKeys.map(key => formValues[key]?.buttonType);
    return BUTTON_TYPE_OPTIONS.filter(item => !otherButtonValues.includes(item.value));
  };

  handleToggleCollapse = name => {
    this.setState({
      [name]: !this.state[name]
    });
  };

  onSelectIconButton = async files => {
    const { prefix, updateField } = this.props;
    let image = null;
    if (files[0].size <= MAX_FILE_SIZE) {
      image = await convertImageToBase64(files[0]);
    }

    updateField(`${prefix}.imgSrc`, files[0]);
    updateField(`${prefix}.imgSize`, files[0].size);
    updateField(`${prefix}.imgName`, files[0].name);
    updateField(`${prefix}.imgSrc64`, image);
  };

  // When change button type -> replace all setting of this type
  handleChangeButtonType = value => {
    const {
      prefix,
      updateField,
      themeSettingDefaultValue,
      themeSettingDefaultThemeValue,
      formValues
    } = this.props;

    let [prefixElement] = prefix.split('_');
    const [prefixImage] = MENU_ID.PRODUCT_IMAGE.split('_');
    // Change prefix area into not area. Ex: productImageMiddle -> productImage
    if (prefixElement.includes(prefixImage)) {
      prefixElement = prefixImage;
    }
    // Other case is product information do nothing

    const keySetting = `${prefixElement}_${value}`;
    const setting = themeSettingDefaultValue[keySetting]
      ? themeSettingDefaultValue[keySetting]
      : themeSettingDefaultThemeValue[keySetting];
    updateField(prefix, { ...setting, position: formValues[prefix]?.position });
  };

  handleChangeWidth = value => {
    const { prefix, formValues, updateField } = this.props;
    const unit = _.get(formValues, `${prefix}.widthUnit`, '');
    if (!value) {
      updateField(`${prefix}.widthUnit`, '');
    } else if (isNumeric(value) && !unit) {
      updateField(`${prefix}.widthUnit`, 'px');
    }
  };

  handleUnmountInput = fieldName => {
    const { formValues, updateField } = this.props;
    const text = _.get(formValues, fieldName, '');
    if (checkHasXss(text)) {
      updateField(fieldName, '');
    }
  };

  handleUnmountDropZone = () => {
    if (this.state.errorUpload) {
      const { prefix, updateField } = this.props;
      updateField(`${prefix}.imgSrc`, '');
      updateField(`${prefix}.imgSize`, '');
      updateField(`${prefix}.imgName`, '');
      updateField(`${prefix}.imgSrc64`, '');
      this.setState({
        errorUpload: ''
      });
    }
  };

  render() {
    const { i18n, prefix, formValues, labelTranslation, updateField } = this.props;
    const {
      isOpenOverall,
      isOpenDimension,
      isOpenAlignment,
      isOpenStyling,
      isOpenButtonStyle,
      errorUpload
    } = this.state;

    const buttonType = _.get(formValues, `${prefix}.buttonType`, '');
    const displayType = _.get(formValues, `${prefix}.displayType`, '');
    const buttonIconImage = _.get(formValues, `${prefix}.imgSrc`, '');
    const buttonIconImageSize = _.get(formValues, `${prefix}.imgSize`, 0);
    const buttonWidth = _.get(formValues, `${prefix}.buttonWidth`, '');

    const backgroundColor = _.get(formValues, `${prefix}.backgroundColor`, '');
    const backgroundColorOnHover = _.get(formValues, `${prefix}.backgroundColorOnHover`, '');
    const textColor = _.get(formValues, `${prefix}.textColor`, '');
    const textColorOnHover = _.get(formValues, `${prefix}.textColorOnHover`, '');
    const borderColor = _.get(formValues, `${prefix}.borderColor`, '');
    const borderColorOnHover = _.get(formValues, `${prefix}.borderColorOnHover`, '');

    const formattedShowButton = SHOW_BUTTON_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const filterButtonTypeOptions = this.getFilterButtonTypeOptions();
    const formattedButtonType = (filterButtonTypeOptions || []).map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedDisplayType = DISPLAY_TYPE_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedSelectOptionAction = SELECT_OPTIONS_ACTION_OPTIONS.filter(item => {
      if (
        [ELEMENTS.PRODUCT_INFORMATION_BUTTON_1, ELEMENTS.PRODUCT_INFORMATION_BUTTON_2].includes(
          prefix
        )
      ) {
        return item.value !== SELECT_OPTIONS_ACTION_VALUE.QUICK_ADD_TO_CART;
      }
      return true;
    }).map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedButtonShape = BUTTON_SHAPE_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedIconPosition = ICON_POSITION_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const formattedButtonWidth = BUTTON_WIDTH_OPTIONS.map(item => {
      return {
        ...item,
        label: i18n.translate(item.label)
      };
    });

    const colorPickerList = [
      {
        colorValue: backgroundColor,
        fieldName: `${prefix}.backgroundColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.button_BackgroundColor_Item')
      },
      {
        colorValue: backgroundColorOnHover,
        fieldName: `${prefix}.backgroundColorOnHover`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.button_BackgroundColorHover_Item')
      },
      {
        colorValue: textColor,
        fieldName: `${prefix}.textColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.button_TextColor_Item'),
        hidden: displayType && displayType === DISPLAY_TYPE_VALUE.ICON_ONLY
      },
      {
        colorValue: textColorOnHover,
        fieldName: `${prefix}.textColorOnHover`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.button_TextColorHover_Item'),
        hidden: displayType && displayType === DISPLAY_TYPE_VALUE.ICON_ONLY
      },
      {
        colorValue: borderColor,
        fieldName: `${prefix}.borderColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.button_BorderColor_Item')
      },
      {
        colorValue: borderColorOnHover,
        fieldName: `${prefix}.borderColorOnHover`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.button_BorderColorHover_Item')
      }
    ];

    const mapGuide = {
      [BUTTON_TYPE_VALUE.SELECTION_ADD_TO_CART]: i18n.translate(
        'AdminHTML.ThemeSetting.Form.productSelectBtn_UserGuide'
      ),
      [BUTTON_TYPE_VALUE.QUICK_VIEW]: i18n.translate(
        'AdminHTML.ThemeSetting.Form.productQuickViewBtn_UserGuide'
      ),
      [BUTTON_TYPE_VALUE.VIEW_PRODUCT]: i18n.translate(
        'AdminHTML.ThemeSetting.Form.productViewProductBtn_UserGuide'
      )
    };

    return (
      <div style={{ marginTop: '1rem' }}>
        <div style={{ marginBottom: '20px' }}>
          <span className="form-link">
            <TextStyle variation="subdued">{mapGuide[buttonType]}</TextStyle>{' '}
            <Link url={URL['ProductButtonThemeSetting']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        {/* Overall */}
        <div className="form-section form-section--bordered">
          <div
            className="section-title-collapse__wrapper"
            onClick={() => this.handleToggleCollapse('isOpenOverall')}>
            <span className="section-title">
              {i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_Title')}
            </span>
            <span className={`caret ${!isOpenOverall ? 'closed' : ''}`}>
              <Icon source={CaretDownMinor} color="base" />
            </span>
          </div>

          <Collapse isOpened={isOpenOverall}>
            {/* Button Type */}
            <div className="form-section__item">
              <Field
                name={`${prefix}.buttonType`}
                component={RenderSelect2}
                options={formattedButtonType}
                label={i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_Type_Item')}
                components={{ DropdownIndicator }}
                styles={styles}
                isSearchable={false}
                onChange={this.handleChangeButtonType}
                isDeepCompare
              />
            </div>

            {/* Show button */}
            <div className="form-section__item form-section__item--medium-spacing">
              <Field
                name={`${prefix}.showOn`}
                component={RenderSelect2}
                options={formattedShowButton}
                label={i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_Show_Item')}
                components={{ DropdownIndicator }}
                styles={styles}
                isSearchable={false}
              />
            </div>

            {/* Show button when hovering */}
            <div className="form-section__item form-section__item--medium-spacing">
              <Field
                name={`${prefix}.showOnHovering`}
                component={RenderCheckbox}
                label={i18n.translate(
                  'AdminHTML.ThemeSetting.Form.button_Overall_ShowWhenHovering'
                )}
                anotherProps={{
                  helpText: i18n.translate(
                    'AdminHTML.ThemeSetting.Form.button_Overall_ShowWhenHoveringNote'
                  )
                }}
              />
            </div>

            {/* "Select options" action onclick */}
            {buttonType && buttonType === BUTTON_TYPE_VALUE.SELECTION_ADD_TO_CART && (
              <div className="form-section__item form-section__item--medium-spacing">
                <Field
                  name={`${prefix}.action`}
                  component={RenderSelect2}
                  options={formattedSelectOptionAction}
                  label={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.button_Overall_SelectOptionAction_Item'
                  )}
                  components={{ DropdownIndicator }}
                  styles={styles}
                  isSearchable={false}
                />

                <div style={{ marginTop: '4px' }}>
                  <TextStyle variation="subdued">
                    {i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_SelectOptionAction_Note'
                    )}
                  </TextStyle>
                </div>
              </div>
            )}
          </Collapse>
        </div>

        {/* Button Style */}
        <div className="form-section form-section--bordered">
          <div
            className="section-title-collapse__wrapper"
            onClick={() => this.handleToggleCollapse('isOpenButtonStyle')}>
            <span className="section-title">
              {i18n.translate('AdminHTML.ThemeSetting.Form.button_ButtonStyle_Title')}
            </span>
            <span className={`caret ${!isOpenButtonStyle ? 'closed' : ''}`}>
              <Icon source={CaretDownMinor} color="base" />
            </span>
          </div>

          <Collapse isOpened={isOpenButtonStyle}>
            {/* Display Type */}
            <div className="form-section__item">
              <Field
                name={`${prefix}.displayType`}
                component={RenderSelect2}
                options={formattedDisplayType}
                label={i18n.translate(
                  'AdminHTML.ThemeSetting.Form.button_Overall_DisplayType_Item'
                )}
                components={{ DropdownIndicator }}
                styles={styles}
                isSearchable={false}
              />
              {displayType === DISPLAY_TYPE_VALUE.TEXT_ICON && (
                <div style={{ marginTop: '4px' }}>
                  <TextStyle variation="subdued">
                    {i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_DisplayType_NoteIconMobile'
                    )}
                  </TextStyle>
                </div>
              )}
            </div>

            {/* Button Shape */}
            <div className="form-section__item form-section__item--medium-spacing">
              <Field
                name={`${prefix}.shape`}
                component={RenderSelect2}
                options={formattedButtonShape}
                label={i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_Shape_Item')}
                components={{ DropdownIndicator }}
                styles={styles}
                isSearchable={false}
              />
            </div>

            {/* Quick view text */}
            {displayType &&
              displayType !== DISPLAY_TYPE_VALUE.ICON_ONLY &&
              buttonType &&
              buttonType === BUTTON_TYPE_VALUE.QUICK_VIEW && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <Field
                    name={`${prefix}.quickViewText`}
                    component={RenderTextField}
                    label={i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_QuickView_Item'
                    )}
                    placeholder={
                      labelTranslation?.qvBtnLabelV2?.value ||
                      i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_QuickView_Filled')
                    }
                    validate={[nonXSSValue]}
                    onUnmount={() => this.handleUnmountInput(`${prefix}.quickViewText`)}
                  />
                </div>
              )}

            {/* View product text */}
            {displayType &&
              displayType !== DISPLAY_TYPE_VALUE.ICON_ONLY &&
              buttonType &&
              buttonType === BUTTON_TYPE_VALUE.VIEW_PRODUCT && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <Field
                    name={`${prefix}.viewProductText`}
                    component={RenderTextField}
                    label={i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_ViewProduct_Item'
                    )}
                    placeholder={
                      labelTranslation?.viewProductBtnLabelV2?.value ||
                      i18n.translate(
                        'AdminHTML.ThemeSetting.Form.button_Overall_ViewProduct_Filled'
                      )
                    }
                    validate={[nonXSSValue]}
                    onUnmount={() => this.handleUnmountInput(`${prefix}.viewProductText`)}
                  />
                </div>
              )}

            {/* Add to cart text */}
            {displayType &&
              displayType !== DISPLAY_TYPE_VALUE.ICON_ONLY &&
              buttonType &&
              buttonType === BUTTON_TYPE_VALUE.SELECTION_ADD_TO_CART && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <Field
                    name={`${prefix}.addToCartText`}
                    component={RenderTextField}
                    label={i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_AddToCart_Item'
                    )}
                    placeholder={
                      labelTranslation?.atcAvailableLabelV2?.value ||
                      i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_AddToCart_Filled')
                    }
                    validate={[nonXSSValue]}
                    onUnmount={() => this.handleUnmountInput(`${prefix}.addToCartText`)}
                  />
                </div>
              )}

            {/* Select option text */}
            {displayType &&
              displayType !== DISPLAY_TYPE_VALUE.ICON_ONLY &&
              buttonType &&
              buttonType === BUTTON_TYPE_VALUE.SELECTION_ADD_TO_CART && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <Field
                    name={`${prefix}.selectOptionText`}
                    component={RenderTextField}
                    label={i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_SelectionOption_Item'
                    )}
                    placeholder={
                      labelTranslation?.atcSelectOptionsLabelV2?.value ||
                      i18n.translate(
                        'AdminHTML.ThemeSetting.Form.button_Overall_SelectionOption_Filled'
                      )
                    }
                    validate={[nonXSSValue]}
                    onUnmount={() => this.handleUnmountInput(`${prefix}.selectOptionText`)}
                  />
                </div>
              )}

            {/* Adding text */}
            {displayType &&
              displayType !== DISPLAY_TYPE_VALUE.ICON_ONLY &&
              buttonType &&
              buttonType === BUTTON_TYPE_VALUE.SELECTION_ADD_TO_CART && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <Field
                    name={`${prefix}.addingText`}
                    component={RenderTextField}
                    label={i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_AddingText_Item'
                    )}
                    placeholder={
                      labelTranslation?.atcAddingToCartBtnLabelV2?.value ||
                      i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_AddingText_Filled')
                    }
                    validate={[nonXSSValue]}
                    onUnmount={() => this.handleUnmountInput(`${prefix}.addingText`)}
                  />
                </div>
              )}

            {/* Added text */}
            {displayType &&
              displayType !== DISPLAY_TYPE_VALUE.ICON_ONLY &&
              buttonType &&
              buttonType === BUTTON_TYPE_VALUE.SELECTION_ADD_TO_CART && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <Field
                    name={`${prefix}.addedText`}
                    component={RenderTextField}
                    label={i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_AddedText_Item'
                    )}
                    placeholder={
                      labelTranslation?.atcAddedToCartBtnLabelV2?.value ||
                      i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_AddedText_Filled')
                    }
                    validate={[nonXSSValue]}
                    onUnmount={() => this.handleUnmountInput(`${prefix}.addedText`)}
                  />
                </div>
              )}

            {/* Sold out text */}
            {displayType &&
              displayType !== DISPLAY_TYPE_VALUE.ICON_ONLY &&
              buttonType &&
              buttonType === BUTTON_TYPE_VALUE.SELECTION_ADD_TO_CART && (
                <div className="form-section__item form-section__item--medium-spacing">
                  <Field
                    name={`${prefix}.soldOutText`}
                    component={RenderTextField}
                    label={i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Overall_SoldOut_Item'
                    )}
                    placeholder={
                      labelTranslation?.atcSoldOutLabelV2?.value ||
                      i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_SoldOut_Filled')
                    }
                    validate={[nonXSSValue]}
                    onUnmount={() => this.handleUnmountInput(`${prefix}.soldOutText`)}
                  />
                </div>
              )}

            {/* Icon image */}
            {displayType && displayType !== DISPLAY_TYPE_VALUE.TEXT_ONLY && (
              <div className="form-section__item form-section__item--medium-spacing">
                <div className="title">
                  <span>
                    {i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_IconImage_Item')}
                  </span>
                </div>
                <div style={{ marginTop: 0 }}>
                  <Field
                    name={`${prefix}.imgSrc`}
                    component={CustomDropZoneStandard}
                    allowMultiple={false}
                    onSelectFile={this.onSelectIconButton}
                    accept={ACCEPT_FILE_IMAGE_TYPE}
                    fileTypes={ACCEPT_FILE_IMAGE_TYPE.split(', ')}
                    defaultFile={buttonIconImage}
                    defaultFileSize={buttonIconImageSize}
                    validate={[limitSizeValidation]}
                    onError={error => this.setState({ errorUpload: error })}
                    onUnmount={this.handleUnmountDropZone}
                  />
                </div>
                <div style={{ marginTop: '4px' }}>
                  <TextStyle variation="subdued">
                    {i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_IconImage_Caption')}
                  </TextStyle>
                </div>
                {errorUpload && <TextStyle variation="negative">{errorUpload}</TextStyle>}
              </div>
            )}

            {/* Icon position */}
            {displayType && displayType === DISPLAY_TYPE_VALUE.TEXT_ICON && (
              <div className="form-section__item form-section__item--medium-spacing">
                <div className="title">
                  <span>
                    {i18n.translate('AdminHTML.ThemeSetting.Form.button_Overall_IconPosition_Item')}
                  </span>
                </div>
                <div className="radio-button-wrapper radio-button-wrapper--no-gap radio-button-wrapper--bordered">
                  {formattedIconPosition.map((item, index) => (
                    <Field
                      key={index}
                      name={`${prefix}.iconPosition`}
                      id={item.value}
                      component={RenderRadioButton2}
                      label={item.label}
                    />
                  ))}
                </div>
              </div>
            )}
          </Collapse>
        </div>

        {/* Dimension */}
        <div className="form-section form-section--bordered">
          <div
            className="section-title-collapse__wrapper"
            onClick={() => this.handleToggleCollapse('isOpenDimension')}>
            <span className="section-title">
              {i18n.translate('AdminHTML.ThemeSetting.Form.button_Dimension_Title')}
            </span>
            <span className={`caret ${!isOpenDimension ? 'closed' : ''}`}>
              <Icon source={CaretDownMinor} color="base" />
            </span>
          </div>

          <Collapse isOpened={isOpenDimension}>
            {/* Button width */}
            <div className="form-section__item">
              <Field
                name={`${prefix}.buttonWidth`}
                component={RenderSelect2}
                options={formattedButtonWidth}
                label={i18n.translate(
                  'AdminHTML.ThemeSetting.Form.button_Dimension_ButtonWidth_Item'
                )}
                components={{ DropdownIndicator }}
                styles={styles}
                isSearchable={false}
              />
            </div>

            {/* Width */}
            {buttonWidth === BUTTON_WIDTH_VALUE.MANUAL_CUSTOM && (
              <div className="form-section__item form-section__item--medium-spacing">
                <div className="input-wrapper-suffix">
                  <Field
                    name={`${prefix}.width`}
                    component={RenderTextField}
                    label={i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Dimension_Width_Item'
                    )}
                    normalize={(value, prevValue) => normalizeNumber(value, prevValue)}
                    placeholder={i18n.translate(
                      'AdminHTML.ThemeSetting.Form.button_Dimension_AutoPlaceholder'
                    )}
                    onChange={this.handleChangeWidth}
                  />

                  <div className="select-borderless">
                    <Field
                      name={`${prefix}.widthUnit`}
                      component={RenderSelect2}
                      options={WIDTH_UNIT_OPTIONS}
                      components={{ DropdownIndicator: null }}
                      styles={stylesCustom}
                      isSearchable={false}
                      placeholder="-"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Height */}
            {buttonWidth === BUTTON_WIDTH_VALUE.MANUAL_CUSTOM && (
              <div
                className="form-section__item form-section__item--medium-spacing"
                style={{ position: 'relative', zIndex: 1 }}>
                <Field
                  name={`${prefix}.height`}
                  component={RenderTextField}
                  label={i18n.translate('AdminHTML.ThemeSetting.Form.button_Dimension_Height_Item')}
                  normalize={(value, prevValue) => normalizeNumber(value, prevValue)}
                  placeholder={i18n.translate(
                    'AdminHTML.ThemeSetting.Form.button_Dimension_AutoPlaceholder'
                  )}
                />
              </div>
            )}
          </Collapse>
        </div>

        {/* Alignment */}
        <div className="form-section form-section--bordered">
          <div
            className="section-title-collapse__wrapper"
            onClick={() => this.handleToggleCollapse('isOpenAlignment')}>
            <span className="section-title">
              {i18n.translate('AdminHTML.ThemeSetting.Form.button_Alignment_Title')}
            </span>
            <span className={`caret ${!isOpenAlignment ? 'closed' : ''}`}>
              <Icon source={CaretDownMinor} color="base" />
            </span>
          </div>

          <Collapse isOpened={isOpenAlignment}>
            <div className="form-section__item">
              <p className="title">
                {i18n.translate('AdminHTML.ThemeSetting.Form.button_Alignment_Item')}
              </p>
              <div className="radio-button-wrapper">
                {BUTTON_ALIGNMENT_OPTIONS.map((item, index) => (
                  <Field
                    key={index}
                    name={`${prefix}.alignment`}
                    id={item.value}
                    component={RenderRadioButton2}
                    label={item.icon}
                  />
                ))}
              </div>
            </div>
          </Collapse>
        </div>

        {/* Styling */}
        <div className="form-section form-section--bordered">
          <div
            className="section-title-collapse__wrapper"
            onClick={() => this.handleToggleCollapse('isOpenStyling')}>
            <span className="section-title">
              {i18n.translate('AdminHTML.ThemeSetting.Form.button_Style_Title')}
            </span>
            <span className={`caret ${!isOpenStyling ? 'closed' : ''}`}>
              <Icon source={CaretDownMinor} color="base" />
            </span>
          </div>

          <Collapse isOpened={isOpenStyling}>
            <div className="form-section__item">
              <p className="title title--small">
                {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
              </p>
              {colorPickerList
                .filter(item => !item.hidden)
                .map((item, index) => (
                  <RenderColorPickerItem
                    key={index}
                    colorValue={item.colorValue}
                    fieldName={item.fieldName}
                    fieldLabel={item.fieldLabel}
                    updateField={updateField}
                  />
                ))}
            </div>

            {displayType !== DISPLAY_TYPE_VALUE.ICON_ONLY && (
              <div className="form-section__item">
                <TextForm
                  formValues={formValues}
                  updateField={updateField}
                  prefix={prefix}
                  titleSmall
                  showTextTransform
                />
              </div>
            )}
          </Collapse>
        </div>
      </div>
    );
  }
}

export default withI18n()(ProductButtonForm);

const stylesCustom = getStylesCustom({
  option: {
    padding: '8px',
    textAlign: 'center'
  },
  menu: {
    width: '48px',
    left: '-7px',
    zIndex: 999
  }
});
